import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  useAuth,
  useProfile,
  useQualifications,
  useWorkExperience,
  useReferences,
  useDocuments,
} from '../../hooks';
import { Spinner } from '../../components/ui';
import { ShareProfileModal } from '../share';
import {
  getFieldOfWorkLabel,
  formatLabel,
  VERIFICATION_STATUS_LABEL,
  recordStatus,
  downloadBlob,
} from '../../utils';

const BRAND = '#168c91';
const BRAND_DARK = '#0f172a';
const BRAND_SOFT = '#82d2d1';

const fmt = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

export const ExportProfile: React.FC = () => {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { qualifications, loading: qualLoading } = useQualifications();
  const { workExperience, loading: expLoading } = useWorkExperience();
  const { references, loading: refLoading } = useReferences();
  const { documents, loading: docsLoading } = useDocuments();
  const [emailInput, setEmailInput] = useState('');
  const [showEmailBox, setShowEmailBox] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const cvRef = useRef<HTMLDivElement>(null);

  const loading = profileLoading || qualLoading || expLoading || refLoading || docsLoading;

  const sortedExp = [...workExperience].sort(
    (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const fullName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    user?.email ||
    '';

  const contactEmail = profile?.contact_email || user?.email || '';

  const handleSavePdf = async () => {
    if (!Capacitor.isNativePlatform()) {
      window.print();
      return;
    }
    const node = cvRef.current;
    if (!node) return;
    setSavingPdf(true);
    setPdfError(null);
    try {
      const { renderElementToPdf } = await import('../../utils/saveCvPdf');
      const blob = await renderElementToPdf(node);
      const slug =
        fullName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') || 'cv';
      await downloadBlob(blob, `${slug}-cv.pdf`);
    } catch {
      setPdfError('Could not save the PDF. Try again.');
    } finally {
      setSavingPdf(false);
    }
  };

  const actionBtn =
    'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto';

  return (
    <>
      {/* Action bar */}
      <div className="print:hidden sticky top-14 md:top-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/profile"
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium self-start"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </Link>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
            <button
              onClick={() => setShowEmailBox(v => !v)}
              className={`${actionBtn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email CV
            </button>
            <button
              onClick={() => setShowShareModal(true)}
              className={`${actionBtn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share link
            </button>
            <Link
              to="/profile/shares"
              className={`${actionBtn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
            >
              Shared links
            </Link>
            <button
              type="button"
              onClick={() => void handleSavePdf()}
              disabled={savingPdf}
              className={`${actionBtn} text-white disabled:opacity-60`}
              style={{ background: BRAND }}
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {savingPdf ? 'Saving PDF…' : 'Save as PDF'}
            </button>
          </div>
        </div>
        {showEmailBox && (
          <div className="max-w-4xl mx-auto px-4 pb-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <input
              type="email"
              placeholder="recipient@example.com"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              className="w-full min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              onKeyDown={e => {
                if (e.key === 'Enter' && emailInput.trim()) {
                  const subject = encodeURIComponent(`CV – ${fullName}`);
                  const body = encodeURIComponent(`Hi,\n\nPlease find my CV attached.\n\nKind regards,\n${fullName}`);
                  window.open(`mailto:${emailInput.trim()}?subject=${subject}&body=${body}`, '_blank');
                }
              }}
            />
            <button
              onClick={() => {
                if (!emailInput.trim()) return;
                const subject = encodeURIComponent(`CV – ${fullName}`);
                const body = encodeURIComponent(`Hi,\n\nPlease find my CV attached.\n\nKind regards,\n${fullName}`);
                window.open(`mailto:${emailInput.trim()}?subject=${subject}&body=${body}`, '_blank');
              }}
              disabled={!emailInput.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 transition-colors shrink-0"
              style={{ background: BRAND }}
            >
              Open Mail App
            </button>
            <p className="text-xs text-gray-400 hidden sm:block">Download the PDF first, then attach it.</p>
          </div>
        )}
        {pdfError && (
          <p className="max-w-4xl mx-auto px-4 pb-3 text-sm text-red-600">{pdfError}</p>
        )}
      </div>

      {/* CV Document */}
      <div className="max-w-4xl mx-auto py-6 sm:py-8 print:p-0 print:max-w-none overflow-x-hidden">
        <div
          ref={cvRef}
          data-cv-export
          className="bg-white print:shadow-none overflow-hidden"
          style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
        >
          {/* ── Header band ── */}
          <div
            className="px-4 py-6 sm:px-10 sm:py-8 print:px-10 print:py-8"
            style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)` }}
          >
            <div className="flex items-start gap-3 sm:gap-6 min-w-0">
              {profile?.photo_url ? (
                <img
                  src={profile.photo_url}
                  alt={fullName}
                  crossOrigin="anonymous"
                  className="cv-photo w-16 h-16 sm:w-[88px] sm:h-[88px] rounded-full object-cover shrink-0"
                  style={{ border: '3px solid rgba(255,255,255,0.4)' }}
                />
              ) : (
                <div
                  className="cv-photo w-16 h-16 sm:w-[88px] sm:h-[88px] rounded-full shrink-0 flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '3px solid rgba(255,255,255,0.3)' }}
                >
                  <span className="cv-initial text-white text-2xl sm:text-[32px] font-bold">{fullName.charAt(0).toUpperCase()}</span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="text-white text-xl sm:text-[28px] font-extrabold m-0 leading-tight break-words">{fullName}</h1>
                {profile?.field_of_work && (
                  <p className="text-sm font-medium mt-1 mb-2" style={{ color: BRAND_SOFT }}>
                    {getFieldOfWorkLabel(profile.field_of_work)}
                  </p>
                )}
                <div className="cv-contacts flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-x-5 sm:gap-y-1.5 mt-2">
                  {contactEmail && (
                    <span className="text-[12px] flex items-start gap-1.5 min-w-0 break-all" style={{ color: 'rgba(239,252,252,0.9)' }}>
                      <svg className="w-3 h-3 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {contactEmail}
                    </span>
                  )}
                  {profile?.phone && (
                    <span className="text-[12px] flex items-center gap-1.5" style={{ color: 'rgba(239,252,252,0.9)' }}>
                      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {profile.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="px-4 py-6 sm:px-10 sm:py-8 print:px-10 print:py-8 flex flex-col gap-7">

            {/* Profile Summary */}
            {profile?.bio && (
              <section>
                <SectionHeading title="Profile Summary" />
                <p style={{ color: '#374151', fontSize: 13, lineHeight: 1.7, margin: 0 }}>{profile.bio}</p>
              </section>
            )}

            {/* Work Experience */}
            {sortedExp.length > 0 && (
              <section>
                <SectionHeading title="Work Experience" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {sortedExp.map(exp => (
                    <div key={exp.id} style={{ display: 'flex', gap: 16 }}>
                      {/* Timeline dot */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: BRAND, flexShrink: 0 }} />
                        <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />
                      </div>
                      <div style={{ flex: 1, paddingBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{exp.job_title}</span>
                          <span style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
                            {fmt(exp.start_date)} – {exp.is_current ? 'Present' : exp.end_date ? fmt(exp.end_date) : ''}
                          </span>
                        </div>
                        <p style={{ fontSize: 12, color: '#4b5563', margin: '2px 0 0', fontWeight: 500 }}>
                          {exp.employer}{exp.location ? ` · ${exp.location}` : ''}
                        </p>
                        {exp.description && (
                          <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 0', lineHeight: 1.6 }}>{exp.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Qualifications */}
            {qualifications.length > 0 && (
              <section>
                <SectionHeading title="Qualifications & Certifications" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {qualifications.map(qual => {
                    const status = recordStatus(qual);
                    return (
                    <div key={qual.id} style={{ display: 'flex', gap: 16, paddingBottom: 10, borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ paddingTop: 4 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: status === 'verified' || status === 'manually_confirmed' ? '#059669' : '#d97706', flexShrink: 0 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{qual.title}</span>
                          <span style={{ fontSize: 10, color: '#374151', background: '#f3f4f6', padding: '2px 7px', borderRadius: 9999, fontWeight: 600 }}>
                            {VERIFICATION_STATUS_LABEL[status]}
                          </span>
                        </div>
                        <p style={{ fontSize: 12, color: '#4b5563', margin: '2px 0 0' }}>
                          {qual.institution}
                          {qual.qualification_type ? ` · ${formatLabel(qual.qualification_type)}` : ''}
                        </p>
                        <div style={{ display: 'flex', gap: 12, marginTop: 3, flexWrap: 'wrap' }}>
                          {qual.issue_date && (
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>Issued: {fmt(qual.issue_date)}</span>
                          )}
                          {qual.expiry_date && (
                            <span style={{ fontSize: 11, color: new Date(qual.expiry_date) < new Date() ? '#ef4444' : '#9ca3af' }}>
                              Expires: {fmt(qual.expiry_date)}{new Date(qual.expiry_date) < new Date() ? ' (Expired)' : ''}
                            </span>
                          )}
                          {qual.credential_id && (
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>ID: {qual.credential_id}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* References */}
            {references.length > 0 && (
              <section>
                <SectionHeading title="References" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                  {references.map(ref => {
                    const status = recordStatus(ref);
                    return (
                    <div key={ref.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', background: '#fafafa' }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#111827', margin: 0 }}>{ref.reference_name}</p>
                      {ref.job_title && <p style={{ fontSize: 11, color: '#4b5563', margin: '2px 0 0' }}>{ref.job_title}</p>}
                      {ref.company && <p style={{ fontSize: 11, color: '#4b5563', margin: 0 }}>{ref.company}</p>}
                      {ref.relationship && <p style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', margin: '4px 0 0' }}>{ref.relationship}</p>}
                      {ref.reference_email && (
                        <p style={{ fontSize: 11, color: BRAND, margin: '4px 0 0' }}>{ref.reference_email}</p>
                      )}
                      {ref.reference_phone && (
                        <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>{ref.reference_phone}</p>
                      )}
                      <span style={{ display: 'inline-block', marginTop: 4, fontSize: 10, color: '#374151', fontWeight: 600 }}>
                        {VERIFICATION_STATUS_LABEL[status]}
                      </span>
                    </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Footer */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>
                {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              <span style={{ fontSize: 10, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Generated by
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <img
                    src="/wandawazi-logo.png"
                    alt=""
                    style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                  />
                  <span style={{ color: BRAND, fontWeight: 800, fontSize: 11, letterSpacing: '0.06em' }}>
                    WANDAWAZI
                  </span>
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; }
          @page { margin: 0; size: A4; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>

      <ShareProfileModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        userId={user?.id ?? ''}
        profile={profile}
        workExperience={workExperience}
        qualifications={qualifications}
        references={references}
        documents={documents}
      />
    </>
  );
};

const SectionHeading: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
    <div style={{ width: 4, height: 18, borderRadius: 2, background: BRAND, flexShrink: 0 }} />
    <h2 style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#374151', margin: 0 }}>
      {title}
    </h2>
    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
  </div>
);
